import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

function StatCard({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="stat-card"
            className={cn('bg-bg-surface border border-border-default rounded-lg p-5', className)}
            {...props}
        />
    );
}

function StatLabel({ className, ...props }: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="stat-label"
            className={cn(
                'text-xs font-semibold tracking-wider uppercase text-text-secondary mb-1',
                className,
            )}
            {...props}
        />
    );
}

interface IStatValueProps extends React.ComponentProps<'div'> {
    /** Render a skeleton in place of the value while its query is in flight. */
    isLoading?: boolean;
    /**
     * Shown muted when `children` is empty and `isLoading` is false — i.e. the
     * query SETTLED without a reading. Keeping that distinct from the loading
     * skeleton matters: a card that renders a skeleton for both leaves a grey
     * bar sitting where the number belongs forever when a request fails.
     */
    placeholder?: React.ReactNode;
    /** Sizes the loading skeleton to the value it stands in for. */
    loadingClassName?: string;
}

/**
 * The big number on a stat card, with its three states kept apart: loading,
 * settled-but-empty, and a value. `isLoading`/`placeholder` are optional, so
 * every existing `<StatValue>{x}</StatValue>` call site behaves exactly as
 * before.
 */
function StatValue({
    className,
    isLoading,
    placeholder,
    loadingClassName,
    children,
    ...props
}: IStatValueProps) {
    if (isLoading) {
        return <Skeleton className={cn('mt-1 h-8 w-24', loadingClassName)} />;
    }

    const isEmpty = children === null || children === undefined || children === '';
    const showPlaceholder = isEmpty && placeholder !== undefined && placeholder !== null;

    return (
        <div
            data-slot="stat-value"
            className={cn(
                'text-2xl font-bold tracking-tight text-text-heading',
                showPlaceholder && 'text-text-tertiary',
                className,
            )}
            {...props}
        >
            {showPlaceholder ? placeholder : children}
        </div>
    );
}

function StatTrend({
    className,
    direction,
    ...props
}: React.ComponentProps<'div'> & {
    direction?: 'up' | 'down';
}) {
    return (
        <div
            data-slot="stat-trend"
            data-direction={direction}
            className={cn(
                'text-[0.8125rem] font-medium mt-1',
                direction === 'up' && 'text-success-500 dark:text-success-500',
                direction === 'down' && 'text-error-500 dark:text-error-500',
                !direction && 'text-text-secondary',
                className,
            )}
            {...props}
        />
    );
}

export { StatCard, StatLabel, StatValue, StatTrend };
export type { IStatValueProps };
